import { useEffect, useState } from 'react'
import { CreditCard, Phone, MessageSquare, MapPinPen, Pencil, Plus, X } from 'lucide-react'
import AddressMapDropdown from '../../../components/ERP/AddressMap/AddressMapDropdown'
import Toast from '../../../components/Toast/Toast'
import { useToast } from '../../../hooks/useToast'
import { patientService } from '../../../services/patientApi'
import { formatDay } from './patientFormat'

function digits(value) {
  return String(value || '').replace(/\D/g, '')
}

function monthYear(value) {
  const match = String(value || '').slice(0, 10).match(/^(\d{4})-(\d{2})/)
  if (!match) return ''
  return `${match[2]}/${match[1]}`
}

function toIsoMonth(value) {
  const text = String(value || '').trim()
  if (!text) return null
  const match = text.match(/^(\d{2})\/(\d{4})$/)
  if (!match) return undefined
  return `${match[2]}-${match[1]}-01`
}

function emptyContact() {
  return { name: '', relationship: '', phone: '' }
}

function trimEmergencyContacts(list) {
  const contacts = list.map((item) => ({
    name: item?.name || '',
    relationship: item?.relationship || '',
    phone: item?.phone || ''
  }))
  let end = contacts.length
  while (end > 0) {
    const item = contacts[end - 1]
    if (item.name || item.relationship || item.phone) break
    end -= 1
  }
  return contacts.slice(0, end)
}

function contactsFromChart(chart) {
  const stored = Array.isArray(chart.emergency_contacts) ? chart.emergency_contacts : []
  const contacts = stored.map((item) => ({
    name: item?.name || '',
    relationship: item?.relationship || '',
    phone: item?.phone || ''
  }))
  if (!contacts.some((item) => item.name || item.relationship || item.phone) && (chart.emergency_name || chart.emergency_relationship || chart.emergency_phone)) {
    contacts.push({
      name: chart.emergency_name || '',
      relationship: chart.emergency_relationship || '',
      phone: chart.emergency_phone || ''
    })
  }
  while (contacts.length < 3) contacts.push(emptyContact())
  return contacts
}

function fromChart(chart) {
  return {
    phone: chart.phone || '',
    whatsapp: chart.whatsapp || '',
    email: chart.email || '',
    emergencyContacts: contactsFromChart(chart),
    address: chart.address || '',
    cep: chart.address_cep || '',
    latitude: chart.address_latitude,
    longitude: chart.address_longitude,
    insuranceProvider: chart.insurance_provider || '',
    insurancePlan: chart.insurance_plan || '',
    insuranceCard: chart.insurance_card || '',
    insuranceValid: monthYear(chart.insurance_valid_until)
  }
}

function EditActions({ editing, saving, onEdit, onCancel, onSave, icon: Icon = Pencil }) {
  if (!editing) {
    return (
      <button type="button" className="patients-icon-btn" onClick={onEdit} aria-label="Editar">
        <Icon size={15} />
      </button>
    )
  }
  return (
    <div className="patients-contact-edit-actions">
      <button type="button" className="patients-icon-btn" onClick={onCancel} disabled={saving} aria-label="Cancelar">
        <X size={15} />
      </button>
      <button type="button" className="patients-btn patients-btn--primary patients-btn--compact" onClick={onSave} disabled={saving}>
        {saving ? 'Salvando...' : 'Salvar'}
      </button>
    </div>
  )
}

function PatientContactsTab({ chart, archived, canEdit, onSaved }) {
  const [form, setForm] = useState(() => fromChart(chart))
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const { toast, showToast, hideToast } = useToast()
  const allowed = canEdit && !archived

  useEffect(() => {
    setForm(fromChart(chart))
  }, [chart])

  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }))
  }

  const setContact = (index, key) => (event) => {
    const value = event.target.value
    setForm((current) => ({
      ...current,
      emergencyContacts: current.emergencyContacts.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [key]: value } : item
      ))
    }))
  }

  const begin = (section) => {
    setForm(fromChart(chart))
    setEditing(section)
  }

  const cancel = () => {
    setForm(fromChart(chart))
    setEditing(null)
  }

  const save = async () => {
    const insuranceValidUntil = editing === 'insurance'
      ? toIsoMonth(form.insuranceValid)
      : (String(chart.insurance_valid_until || '').slice(0, 10).match(/^\d{4}-\d{2}-\d{2}/)?.[0] || null)
    if (insuranceValidUntil === undefined) {
      showToast('Informe a validade como MM/AAAA.', 'error')
      return
    }
    setSaving(true)
    try {
      await patientService.updateContacts(chart.id, {
        phone: form.phone,
        whatsapp: form.whatsapp,
        email: form.email,
        emergencyContacts: trimEmergencyContacts(form.emergencyContacts),
        address: form.address,
        addressCep: form.cep,
        addressLatitude: form.latitude,
        addressLongitude: form.longitude,
        insuranceProvider: form.insuranceProvider,
        insurancePlan: form.insurancePlan,
        insuranceCard: form.insuranceCard,
        insuranceValidUntil
      })
      setEditing(null)
      showToast('Contatos atualizados.', 'success')
      onSaved()
    } catch (err) {
      showToast(err.response?.data?.message || 'Não foi possível salvar os contatos', 'error')
    } finally {
      setSaving(false)
    }
  }

  const whatsapp = digits(form.whatsapp || form.phone)
  const channelsOpen = editing === 'channels'
  const emergencyOpen = editing === 'emergency'
  const addressOpen = editing === 'address'
  const insuranceOpen = editing === 'insurance'

  return (
    <div className="patients-split">
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={hideToast} />
      <section className="patients-panel">
        <div className="patients-section-head">
          <h3 className="patients-inline"><Phone size={16} className="tone-green" /> Canais do paciente</h3>
          {allowed && (
            <EditActions
              editing={channelsOpen}
              saving={saving}
              onEdit={() => begin('channels')}
              onCancel={cancel}
              onSave={save}
            />
          )}
        </div>
        <div className="patients-contact-channels">
          <div className="patients-contact-channel">
            <span className="patients-kicker">Telefone</span>
            {channelsOpen ? (
              <input value={form.phone} onChange={set('phone')} placeholder="(00) 0000-0000" />
            ) : (
              <strong>{chart.phone || '—'}</strong>
            )}
            {(channelsOpen ? form.phone : chart.phone) && (
              <a href={`tel:${digits(channelsOpen ? form.phone : chart.phone)}`} className="patients-btn patients-btn--ghost patients-btn--compact">Ligar</a>
            )}
          </div>
          <div className="patients-contact-channel patients-contact-channel--green">
            <span className="patients-kicker">WhatsApp</span>
            {channelsOpen ? (
              <input value={form.whatsapp} onChange={set('whatsapp')} placeholder="(00) 00000-0000" />
            ) : (
              <strong>{chart.whatsapp || '—'}</strong>
            )}
            {whatsapp && (
              <a href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Olá ${chart.name}, informamos sobre o prontuário ${chart.record_number}.`)}`} target="_blank" rel="noopener noreferrer" className="patients-btn patients-btn--green patients-btn--compact">
                <MessageSquare size={14} /> WhatsApp
              </a>
            )}
          </div>
          <div className="patients-contact-channel">
            <span className="patients-kicker">E-mail</span>
            {channelsOpen ? (
              <input type="email" value={form.email} onChange={set('email')} placeholder="email@exemplo.com" />
            ) : (
              <strong>{chart.email || '—'}</strong>
            )}
          </div>
        </div>
        <div className="patients-section-head" style={{ marginTop: '1rem' }}>
          <h3 className="patients-inline">Contato de emergência</h3>
          {allowed && (
            <EditActions
              editing={emergencyOpen}
              saving={saving}
              onEdit={() => begin('emergency')}
              onCancel={cancel}
              onSave={save}
            />
          )}
        </div>
        <div className="patients-emergency-list">
          {form.emergencyContacts.map((contact, index) => (
            <article className="patients-emergency-card" key={`emergency-${index}`}>
              {emergencyOpen && form.emergencyContacts.length > 3 && (
                <button
                  type="button"
                  className="patients-emergency-remove"
                  aria-label="Remover contato"
                  onClick={() => setForm((current) => ({
                    ...current,
                    emergencyContacts: current.emergencyContacts.filter((_, itemIndex) => itemIndex !== index)
                  }))}
                >
                  <X size={14} />
                </button>
              )}
              <label>
                <span className="patients-kicker">Nome</span>
                {emergencyOpen ? (
                  <input value={contact.name} onChange={setContact(index, 'name')} placeholder="Nome" />
                ) : (
                  <strong>{contact.name || '—'}</strong>
                )}
              </label>
              <label>
                <span className="patients-kicker">Parentesco</span>
                {emergencyOpen ? (
                  <input value={contact.relationship} onChange={setContact(index, 'relationship')} placeholder="Parentesco" />
                ) : (
                  <strong>{contact.relationship || '—'}</strong>
                )}
              </label>
              <label>
                <span className="patients-kicker">Telefone</span>
                {emergencyOpen ? (
                  <input value={contact.phone} onChange={setContact(index, 'phone')} placeholder="(00) 00000-0000" />
                ) : (
                  <strong>{contact.phone || '—'}</strong>
                )}
                {contact.phone && (
                  <a href={`tel:${digits(contact.phone)}`} className="patients-btn patients-btn--ghost patients-btn--compact">Ligar</a>
                )}
              </label>
            </article>
          ))}
        </div>
        {emergencyOpen && (
          <button
            type="button"
            className="patients-btn patients-btn--ghost patients-btn--compact"
            style={{ marginTop: '0.65rem' }}
            onClick={() => {
              if (form.emergencyContacts.length >= 12) {
                showToast('É possível cadastrar no máximo 12 contatos de emergência.', 'error')
                return
              }
              setForm((current) => ({
                ...current,
                emergencyContacts: [...current.emergencyContacts, emptyContact()]
              }))
            }}
          >
            <Plus size={14} /> Adicionar contato
          </button>
        )}
      </section>
      <div className="patients-stack">
        <section className="patients-panel">
          <div className="patients-section-head">
            <h3 className="patients-inline">Endereço residencial</h3>
            {allowed && (
              <EditActions
                editing={addressOpen}
                saving={saving}
                icon={MapPinPen}
                onEdit={() => begin('address')}
                onCancel={cancel}
                onSave={save}
              />
            )}
          </div>
          <AddressMapDropdown
            value={{
              address: form.address,
              cep: form.cep,
              latitude: form.latitude,
              longitude: form.longitude
            }}
            readOnly={!addressOpen}
            shareTitle={chart.name}
            onChange={(next) => {
              setForm((current) => ({ ...current, ...next }))
            }}
          />
        </section>
        <section className="patients-panel">
          <div className="patients-section-head">
            <h3 className="patients-inline"><CreditCard size={16} className="tone-green" /> Carteira do convênio</h3>
            {allowed && (
              <EditActions
                editing={insuranceOpen}
                saving={saving}
                onEdit={() => begin('insurance')}
                onCancel={cancel}
                onSave={save}
              />
            )}
          </div>
          <div className="patients-wallet">
            {insuranceOpen ? (
              <div className="patients-wallet-grid">
                <label>
                  Operadora
                  <input value={form.insuranceProvider} onChange={set('insuranceProvider')} placeholder="Ex: Unimed" />
                </label>
                <label>
                  Plano
                  <input value={form.insurancePlan} onChange={set('insurancePlan')} placeholder="Ex: Top Nacional" />
                </label>
                <label>
                  Carteirinha
                  <input value={form.insuranceCard} onChange={set('insuranceCard')} />
                </label>
                <label>
                  Validade
                  <input value={form.insuranceValid} onChange={set('insuranceValid')} placeholder="MM/AAAA" inputMode="numeric" />
                </label>
              </div>
            ) : (
              <>
                <div className="patients-wallet-top">
                  <span>{chart.insurance_provider || 'Particular'}</span>
                  <span className="tone-green">{chart.insurance_plan || 'Sem plano'}</span>
                </div>
                <div>
                  <span>Número da carteirinha</span>
                  <strong>{chart.insurance_card || '—'}</strong>
                </div>
                <div className="patients-wallet-bottom">
                  <span>Titular: {chart.name}</span>
                  <span>Validade: {formatDay(chart.insurance_valid_until)}</span>
                </div>
              </>
            )}
          </div>
          {chart.customer_name && <p className="patients-muted" style={{ marginTop: '0.75rem' }}>Cliente financeiro: {chart.customer_name}</p>}
        </section>
      </div>
    </div>
  )
}

export default PatientContactsTab
